/**
 * CWS-1882: a prepaid redemption no longer creates an order, so the `vehicle_washes` SSE
 * payload for one carries `order_id` = the PURCHASE order id plus a `prepaid_redemption`
 * block. That purchase order may not be a tunnel row at all (it was sold at the counter,
 * possibly on another day), so flashing it as a brand new arrival would highlight nothing
 * and wrongly steal the "new tile" state. For every other event the id belongs to an order
 * that is about to appear in the list, so it is highlighted even though it is not there yet.
 */

/** The parts of a `vehicle_washes` SSE payload this decision reads. */
export interface VehicleWashEventLike {
  order_id?: string | null;
  prepaid_redemption?: Record<string, any> | null;
}

export interface VehicleWashHighlightDecision {
  /** The order id carried by the event, trimmed ('' when absent). */
  incomingOrderId: string;
  /** True when the event describes a prepaid redemption visit. */
  isPrepaidRedemption: boolean;
  /** Row id to flash as "new", or null when nothing should be flashed. */
  highlightOrderId: string | null;
}

function normalizeId(value: unknown): string {
  return String(value ?? '').trim();
}

/**
 * Decides which order id (if any) the tunnel list should highlight for a wash event.
 * `knownRowIds` are the ids of the rows currently rendered (both `id` and `orderId`).
 */
export function resolveVehicleWashHighlight(
  payload: VehicleWashEventLike | null | undefined,
  knownRowIds: Iterable<string> = []
): VehicleWashHighlightDecision {
  const incomingOrderId = normalizeId(payload?.order_id);
  const isPrepaidRedemption = !!payload?.prepaid_redemption;

  if (!incomingOrderId) {
    return { incomingOrderId: '', isPrepaidRedemption, highlightOrderId: null };
  }

  if (!isPrepaidRedemption) {
    return { incomingOrderId, isPrepaidRedemption, highlightOrderId: incomingOrderId };
  }

  const known = new Set<string>();
  for (const rowId of knownRowIds) {
    const id = normalizeId(rowId);
    if (id) {
      known.add(id);
    }
  }

  return {
    incomingOrderId,
    isPrepaidRedemption,
    highlightOrderId: known.has(incomingOrderId) ? incomingOrderId : null,
  };
}
